import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Close, Delete, Edit } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import apiClient from "../api/client";
import { useModulePermission } from "../utils/useModulePermission";


interface ConversionRatePageProps {
  startDate: string | null;
  endDate: string | null;
}

interface Currency {
  id: number;
  code: string;
  name: string;
  symbol?: string;
  country?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface ExchangeRateItem {
  id: number;
  from_currency_id: number;
  to_currency_id: number;
  rate: string;
  effective_date: string;
  source: string;
  is_latest: boolean;
  created_by?: number;
  created_at: string;
  from_currency_code: string;
  to_currency_code: string;
}

interface ExchangeRatesListResponse {
  items: ExchangeRateItem[];
  page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
}

const formatApiError = (error: unknown): string => {
  if (!error) return "An unknown error occurred";
  if (typeof error === "string") return error;
  const err = error as {
    detail?: unknown;
    message?: string;
  };
  if (Array.isArray(err.detail)) {
    return err.detail
      .map((d: { loc?: unknown[]; msg?: string }) => {
        const field =
          d.loc && Array.isArray(d.loc) && d.loc.length > 1
            ? String(d.loc[d.loc.length - 1])
            : "field";
        return `${field}: ${d.msg || "Invalid value"}`;
      })
      .join(", ");
  }
  if (typeof err.detail === "string") return err.detail;
  if (err.detail && typeof err.detail === "object") return formatApiError(err.detail);
  if (err.message) return err.message;
  try {
    return JSON.stringify(error);
  } catch {
    return "An error occurred";
  }
};

const currencyOptionLabel = (c: Currency) => `${c.code} — ${c.name}`;

const helperTextErrorSx = { fontSize: "0.75rem", color: "error.main" } as const;

const ConversionRatePage: React.FC<ConversionRatePageProps> = ({
  startDate,
  endDate,
}) => {
  void startDate;
  void endDate;

  const { can } = useModulePermission("Conversion Rate");
  const canCreate = can("write");
  const canUpdate = can("update");
  const canDelete = can("delete");
  const showActionsCol = canUpdate || canDelete;

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [currenciesError, setCurrenciesError] = useState<string | null>(null);

  const [rows, setRows] = useState<ExchangeRateItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [fromCurrency, setFromCurrency] = useState<Currency | null>(null);
  const [toCurrency, setToCurrency] = useState<Currency | null>(null);
  const [rate, setRate] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [isLatest, setIsLatest] = useState(true);
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const toCurrencyOptions = useMemo(() => {
    if (!fromCurrency) return currencies;
    return currencies.filter((c) => c.id !== fromCurrency.id);
  }, [currencies, fromCurrency]);

  const dialogTitle = useMemo(
    () =>
      editingId === null ? "Add exchange rate" : "Edit exchange rate",
    [editingId],
  );

  const loadCurrencies = useCallback(async () => {
    setCurrenciesLoading(true);
    setCurrenciesError(null);
    try {
      const { data } = await apiClient.get<Currency[]>(
        "/api/v1/finance/currencies",
      );
      setCurrencies(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      const axiosErr = e as { response?: { data?: unknown } };
      setCurrenciesError(
        formatApiError(axiosErr.response?.data) || "Failed to load currencies",
      );
    } finally {
      setCurrenciesLoading(false);
    }
  }, []);

  const fetchExchangeRates = useCallback(
    async (pageNum: number, size: number) => {
      setListLoading(true);
      try {
        const { data } = await apiClient.get<ExchangeRatesListResponse>(
          "/api/v1/finance/exchange-rates",
          { params: { page: pageNum, page_size: size } },
        );
        setRows(Array.isArray(data?.items) ? data.items : []);
        setTotalRecords(
          typeof data?.total_records === "number" ? data.total_records : 0,
        );
      } catch (e: unknown) {
        const axiosErr = e as { response?: { data?: unknown } };
        setSnackbar({
          open: true,
          message:
            formatApiError(axiosErr.response?.data) ||
            "Failed to load exchange rates",
          severity: "error",
        });
        setRows([]);
        setTotalRecords(0);
      } finally {
        setListLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void loadCurrencies();
  }, [loadCurrencies]);

  useEffect(() => {
    void fetchExchangeRates(page, pageSize);
  }, [page, pageSize, fetchExchangeRates]);

  const resetForm = () => {
    setEditingId(null);
    setFromCurrency(null);
    setToCurrency(null);
    setRate("");
    setEffectiveDate("");
    setIsLatest(true);
    setFormSubmitAttempted(false);
  };

  const openCreateDialog = () => {
    if (!canCreate) {
      setSnackbar({
        open: true,
        message: "You do not have permission to create exchange rates.",
        severity: "error",
      });
      return;
    }
    resetForm();
    setEffectiveDate(dayjs().format("YYYY-MM-DD"));
    setDialogOpen(true);
  };

  const openEditDialog = async (row: ExchangeRateItem) => {
    if (!canUpdate) {
      setSnackbar({
        open: true,
        message: "You do not have permission to edit exchange rates.",
        severity: "error",
      });
      return;
    }
    setFormSubmitAttempted(false);
    setEditingId(row.id);
    setDialogOpen(true);
    setDetailLoading(true);
    try {
      let list = currencies;
      if (list.length === 0) {
        const curRes = await apiClient.get<Currency[]>(
          "/api/v1/finance/currencies",
        );
        list = Array.isArray(curRes.data) ? curRes.data : [];
        setCurrencies(list);
      }

      const { data } = await apiClient.get<ExchangeRateItem>(
        `/api/v1/finance/exchange-rates/${row.id}`,
      );
      const from =
        list.find((c) => c.id === data.from_currency_id) ??
        list.find((c) => c.code === data.from_currency_code) ??
        null;
      const to =
        list.find((c) => c.id === data.to_currency_id) ??
        list.find((c) => c.code === data.to_currency_code) ??
        null;
      setFromCurrency(from);
      setToCurrency(to);
      setRate(String(Number(data.rate)));
      setEffectiveDate(data.effective_date?.slice(0, 10) ?? "");
      setIsLatest(Boolean(data.is_latest));
    } catch (e: unknown) {
      const axiosErr = e as { response?: { data?: unknown } };
      setSnackbar({
        open: true,
        message:
          formatApiError(axiosErr.response?.data) ||
          "Failed to load exchange rate details",
        severity: "error",
      });
      setDialogOpen(false);
      resetForm();
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDialog = () => {
    if (saveLoading || detailLoading) return;
    setDialogOpen(false);
    resetForm();
  };

  const buildPayload = () => {
    if (!fromCurrency || !toCurrency) return null;
    const rateNum = Number(rate);
    if (!effectiveDate || !Number.isFinite(rateNum) || rateNum <= 0) return null;
    return {
      from_currency_id: fromCurrency.id,
      to_currency_id: toCurrency.id,
      rate: rateNum,
      effective_date: effectiveDate,
      is_latest: isLatest,
    };
  };

  const saveRow = async () => {
    if (editingId === null && !canCreate) {
      setSnackbar({
        open: true,
        message: "You do not have permission to create exchange rates.",
        severity: "error",
      });
      return;
    }
    if (editingId !== null && !canUpdate) {
      setSnackbar({
        open: true,
        message: "You do not have permission to update exchange rates.",
        severity: "error",
      });
      return;
    }
    const payload = buildPayload();
    if (!payload) {
      setFormSubmitAttempted(true);
      setSnackbar({
        open: true,
        message:
          "Please complete all required fields with valid values.",
        severity: "error",
      });
      return;
    }
    if (fromCurrency && toCurrency && fromCurrency.id === toCurrency.id) {
      setFormSubmitAttempted(true);
      setSnackbar({
        open: true,
        message: "From and to currencies must be different.",
        severity: "error",
      });
      return;
    }

    setSaveLoading(true);
    try {
      if (editingId === null) {
        await apiClient.post("/api/v1/finance/exchange-rates", payload);
        setSnackbar({
          open: true,
          message: "Exchange rate created.",
          severity: "success",
        });
      } else {
        await apiClient.put(
          `/api/v1/finance/exchange-rates/${editingId}`,
          payload,
        );
        setSnackbar({
          open: true,
          message: "Exchange rate updated.",
          severity: "success",
        });
      }
      setDialogOpen(false);
      resetForm();
      await fetchExchangeRates(page, pageSize);
    } catch (e: unknown) {
      const axiosErr = e as { response?: { data?: unknown }; message?: string };
      const status = (e as { response?: { status?: number } }).response?.status;
      if (status === 405 && editingId !== null) {
        try {
          await apiClient.put(
            `/api/v1/finance/exchange-rates/${editingId}`,
            payload,
          );
          setSnackbar({
            open: true,
            message: "Exchange rate updated.",
            severity: "success",
          });
          setDialogOpen(false);
          resetForm();
          await fetchExchangeRates(page, pageSize);
          return;
        } catch (e2: unknown) {
          const ax2 = e2 as { response?: { data?: unknown } };
          setSnackbar({
            open: true,
            message:
              formatApiError(ax2.response?.data) ||
              "Update failed (PUT and PATCH).",
            severity: "error",
          });
          return;
        }
      }
      setSnackbar({
        open: true,
        message:
          formatApiError(axiosErr.response?.data) ||
          axiosErr.message ||
          "Save failed",
        severity: "error",
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (deleteId === null) return;
    if (!canDelete) {
      setSnackbar({
        open: true,
        message: "You do not have permission to delete exchange rates.",
        severity: "error",
      });
      setDeleteId(null);
      return;
    }
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/api/v1/finance/exchange-rates/${deleteId}`);
      setSnackbar({
        open: true,
        message: "Exchange rate deleted.",
        severity: "success",
      });
      setDeleteId(null);
      const newTotal = Math.max(0, totalRecords - 1);
      const maxPage =
        pageSize > 0 ? Math.max(1, Math.ceil(newTotal / pageSize)) : 1;
      const nextPage = Math.min(page, maxPage);
      await fetchExchangeRates(nextPage, pageSize);
      if (nextPage !== page) setPage(nextPage);
    } catch (e: unknown) {
      const axiosErr = e as { response?: { data?: unknown } };
      setSnackbar({
        open: true,
        message:
          formatApiError(axiosErr.response?.data) ||
          "Failed to delete exchange rate",
        severity: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const displayRate = (r: string) => {
    const n = Number(r);
    return Number.isFinite(n) ? n.toLocaleString("en-IN", { maximumFractionDigits: 6 }) : r;
  };

  const rateNumVal = Number(rate);
  const rateInvalid =
    !String(rate).trim() || !Number.isFinite(rateNumVal) || rateNumVal <= 0;
  const fromFieldError = formSubmitAttempted && !fromCurrency;
  const toMissing = formSubmitAttempted && !toCurrency;
  const sameCurrencyError =
    formSubmitAttempted &&
    Boolean(fromCurrency) &&
    Boolean(toCurrency) &&
    fromCurrency!.id === toCurrency!.id;
  const toFieldError = Boolean(toMissing || sameCurrencyError);
  const rateFieldError = formSubmitAttempted && rateInvalid;
  const effectiveDateFieldError = formSubmitAttempted && !effectiveDate;

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Exchange rates
        </Typography>
        <Tooltip
          title={
            !canCreate
              ? "You do not have permission to create exchange rates."
              : ""
          }
        >
          <span>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateDialog}
              disabled={
                currenciesLoading || !!currenciesError || !canCreate
              }
            >
              Add rate
            </Button>
          </span>
        </Tooltip>
      </Box>

      {currenciesError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCurrenciesError(null)}>
          {currenciesError} — currency dropdowns may be empty.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f8fafc" }}>
              <TableCell sx={{ fontWeight: 600 }}>Si.No</TableCell>
              {/* <TableCell sx={{ fontWeight: 600 }}>ID</TableCell> */}
              <TableCell sx={{ fontWeight: 600 }}>From</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>To</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                FX Rate
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Effective date</TableCell>
              {/* <TableCell sx={{ fontWeight: 600 }}>Source</TableCell> */}
              {/* <TableCell sx={{ fontWeight: 600 }}>Latest</TableCell> */}
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              {showActionsCol && (
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {listLoading ? (
              <TableRow>
                <TableCell
                  colSpan={showActionsCol ? 10 : 9}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <CircularProgress size={36} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={showActionsCol ? 10 : 9}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography color="text.secondary">
                    No exchange rates found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    {(page - 1) * pageSize + index + 1}
                  </TableCell>
                  {/* <TableCell>{row.id}</TableCell> */}
                  <TableCell>{row.from_currency_code}</TableCell>
                  <TableCell>{row.to_currency_code}</TableCell>
                  <TableCell align="right">{displayRate(row.rate)}</TableCell>
                  <TableCell>{row.effective_date}</TableCell>
                  {/* <TableCell>{row.source}</TableCell> */}
                  {/* <TableCell>
                    <Chip
                      size="small"
                      label={row.is_latest ? "Yes" : "No"}
                      color={row.is_latest ? "success" : "default"}
                      variant="outlined"
                    />
                  </TableCell> */}
                  <TableCell>
                    {row.created_at
                      ? new Date(row.created_at).toLocaleString()
                      : "—"}
                  </TableCell>
                  {showActionsCol && (
                    <TableCell align="center">
                      {canUpdate && (
                        <Tooltip
                          title={
                            currenciesLoading
                              ? "Loading currencies…"
                              : "Edit"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => void openEditDialog(row)}
                              sx={{ color: "#d97706" }}
                              disabled={currenciesLoading}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setDeleteId(row.id)}
                              sx={{ color: "#dc2626" }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalRecords}
          page={Math.max(0, page - 1)}
          onPageChange={(_, newPage) => setPage(newPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            const next = parseInt(e.target.value, 10);
            setPageSize(Number.isFinite(next) ? next : 10);
            setPage(1);
          }}
          rowsPerPageOptions={[5, 10, 20, 50]}
          showFirstButton
          showLastButton
        />
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {dialogTitle}
          </Typography>
          <IconButton onClick={closeDialog} size="small" disabled={saveLoading}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
                <Autocomplete
                  options={currencies}
                  loading={currenciesLoading}
                  value={fromCurrency}
                  onChange={(_, v) => {
                    setFormSubmitAttempted(false);
                    setFromCurrency(v);
                    if (v && toCurrency && toCurrency.id === v.id) setToCurrency(null);
                  }}
                  getOptionLabel={currencyOptionLabel}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="From currency"
                      required
                      error={fromFieldError}
                      helperText={
                        fromFieldError ? "From currency is required." : undefined
                      }
                      FormHelperTextProps={
                        fromFieldError ? { sx: helperTextErrorSx } : undefined
                      }
                    />
                  )}
                />
                <Autocomplete
                  options={toCurrencyOptions}
                  loading={currenciesLoading}
                  value={toCurrency}
                  onChange={(_, v) => {
                    setFormSubmitAttempted(false);
                    setToCurrency(v);
                  }}
                  getOptionLabel={currencyOptionLabel}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disabled={!fromCurrency}
                  renderInput={(params) => {
                    const toErrorMessage = toFieldError
                      ? sameCurrencyError
                        ? "From and to currencies must be different."
                        : toMissing && fromCurrency
                          ? "To currency is required."
                          : toMissing && !fromCurrency
                            ? "Select from currency, then choose to currency."
                            : undefined
                      : undefined;
                    return (
                      <TextField
                        {...params}
                        label="To currency"
                        required
                        error={Boolean(toFieldError)}
                        helperText={toErrorMessage}
                        FormHelperTextProps={
                          toErrorMessage ? { sx: helperTextErrorSx } : undefined
                        }
                      />
                    );
                  }}
                />
                <TextField
                  label="FX Rate"
                  type="number"
                  value={rate}
                  onChange={(e) => {
                    setFormSubmitAttempted(false);
                    setRate(e.target.value);
                  }}
                  fullWidth
                  required
                  error={rateFieldError}
                  helperText={
                    rateFieldError
                      ? !String(rate).trim()
                        ? "FX Rate is required."
                        : "Enter a valid positive number."
                      : undefined
                  }
                  FormHelperTextProps={
                    rateFieldError ? { sx: helperTextErrorSx } : undefined
                  }
                  inputProps={{ min: 0, step: "0.000001" }}
                />
                <DatePicker
                  label="Effective date"
                  value={effectiveDate ? dayjs(effectiveDate) : null}
                  onChange={(newValue) => {
                    setFormSubmitAttempted(false);
                    setEffectiveDate(
                      newValue && newValue.isValid()
                        ? newValue.format("YYYY-MM-DD")
                        : "",
                    );
                  }}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: effectiveDateFieldError,
                      helperText: effectiveDateFieldError
                        ? "Effective date is required."
                        : undefined,
                      FormHelperTextProps: effectiveDateFieldError
                        ? { sx: helperTextErrorSx }
                        : undefined,
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
                <FormControl
                  required
                  component="fieldset"
                  variant="standard"
                  sx={{ m: 0 }}
                >
                  <FormLabel component="legend" required sx={{ fontSize: "0.875rem" }}>
                    Latest status
                  </FormLabel>
                  <FormControlLabel
                    sx={{ mt: 0.5, ml: 0 }}
                    control={
                      <Switch
                        checked={isLatest}
                        onChange={(e) => {
                          setFormSubmitAttempted(false);
                          setIsLatest(e.target.checked);
                        }}
                      />
                    }
                    label="Mark as latest"
                  />
                </FormControl>
              </Box>
            </LocalizationProvider>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDialog} variant="outlined" disabled={saveLoading}>
            Cancel
          </Button>
          <Button
            onClick={() => void saveRow()}
            variant="contained"
            disabled={
              saveLoading ||
              detailLoading ||
              currenciesLoading ||
              (editingId === null && !canCreate) ||
              (editingId !== null && !canUpdate)
            }
          >
            {saveLoading
              ? editingId !== null
                ? "Updating…"
                : "Saving…"
              : editingId !== null
                ? "Update"
                : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteId !== null}
        onClose={() => {
          if (!deleteLoading) setDeleteId(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Delete this exchange rate? This cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteId(null)}
            variant="outlined"
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void confirmDelete()}
            variant="contained"
            color="error"
            disabled={deleteLoading || !canDelete}
          >
            {deleteLoading ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConversionRatePage;
